import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { ChevronRight } from "lucide-react";
import React, { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import TaskBdField from "./TaskBdField";

import FormInput from "@/components/form-fields/form-input";
import { DragableObject } from "@/components/reusables/Dnd/DragableObject";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { ITaskBreakDownSchema } from "@/schemas/task-and-claims";

const SectionTotal = ({ index }: { index: number }): React.JSX.Element => {
  const form = useFormContext<ITaskBreakDownSchema>();
  const sectionDetails = form.watch(`data.${index}.details`);

  const detailsLength = sectionDetails?.length;

  const sectionTotal = sectionDetails?.reduce((acc: number, item) => {
    // total progress values

    return acc + Number(item?.newProgress);
  }, 0);

  useEffect(() => {
    // average progress values

    const currrentTotal = form.getValues(`data.${index}.total`);
    const newTotal = Number((sectionTotal / detailsLength)?.toFixed(2));
    if (currrentTotal !== newTotal) {
      form.setValue(`data.${index}.total`, newTotal);
    }
  }, [JSON.stringify(sectionDetails), form, sectionDetails]);

  return (
    <FormInput
      form={form}
      name={`data.${index}.total`}
      input={{
        disabled: true,
        placeholder: `Section Total`,
        className: "w-full",
        type: "number",
        required: true,
      }}
    />
  );
};

const TaskBreakdownForm = ({
  isHideItem = false,
}: {
  isHideItem?: boolean;
}): React.JSX.Element => {
  const form = useFormContext<ITaskBreakDownSchema>();
  const { fields, move } = useFieldArray({
    control: form.control,
    name: "data",
    keyName: "sectionId",
  });

  const handleReorder = (e: DragEndEvent): void => {
    if (!e.over) {
      return;
    }

    const oldIndex = fields.findIndex(
      (field) => field.sectionId === e.active.id,
    );
    const newIndex = fields.findIndex(
      (field) => field.sectionId === e.over!.id,
    );

    if (oldIndex !== newIndex) {
      move(oldIndex, newIndex);
      const data = form.getValues("data");
      if (data) {
        // Update positions after reordering
        const updatedFields = data.map((field, index) => ({
          ...field,
          position: index,
        }));

        // Update the form data with new positions
        form.setValue("data", updatedFields);
      }
    }
  };

  return (
    <DndContext onDragEnd={handleReorder}>
      <main
        className={cn(
          "w-full grid text-sm text-primary text-nowrap items-center grid-cols-[0.4fr_2fr_2.5fr_1fr_1fr_1fr_1fr_1.1fr_0.4fr] bg-primaryLight rounded-md gap-2 p-1 dark:text-white overflow-y-auto",
        )}
      >
        <div className='p-2'>
          <Checkbox />
        </div>
        {!isHideItem && <div className='p-2'>ITEMS</div>}
        <div className='p-2'>DESCRIPTION</div>
        <div className='p-2'>UNIT</div>
        <div className='p-2'>QUANTITY </div>
        <div className='p-2'>LAST PROGRESS</div>
        <div className='p-2'>NEW PROGRESS</div>
        <div className='p-2'>CHANGE(%)</div>
        <div className='p-2'></div>
      </main>

      <SortableContext items={fields?.map((field) => field?.sectionId)}>
        {fields
          .sort((a, b) => a.position - b.position)
          ?.map((field, index) => (
            <DragableObject
              key={`data.${field.sectionId}`}
              id={field.sectionId}
            >
              <div className={cn(" w-full grid grid-cols-1")}>
                <Collapsible className='w-full' defaultOpen>
                  <div className=' grid w-full grid-cols-[0.4fr_2fr_2.5fr_1fr_1fr_1fr_1fr_1.1fr_0.4fr] rounded-md gap-4'>
                    <div className='col-span-9 grid grid-cols-[0.4fr_1fr] items-center gap-4'>
                      <div className='flex'>
                        <CollapsibleTrigger className='data-[state=open]:rotate-90'>
                          <ChevronRight
                            className='mr-1 min-w-5 min-h-5 '
                            strokeWidth={2}
                          />
                        </CollapsibleTrigger>
                        <FormInput
                          form={form}
                          name={`data.${index}.name`}
                          className='w-full'
                          input={{
                            disabled: true,
                            placeholder: `Section Name`,
                            type: "text",
                            required: true,
                          }}
                        />
                      </div>

                      <div className='grid grid-cols-[3fr_1fr] items-center'>
                        <div className='w-full h-1 border-0 border-t-2 border-primary/90 ' />
                        <SectionTotal index={index} />
                      </div>
                    </div>
                  </div>

                  {/* <CollapsibleContent key={`${field.sectionId}-fields`}> */}
                  <CollapsibleContent key={`${field}-fields`}>
                    <TaskBdField
                      key={`${field}-${index}`}
                      sectionId={index}
                      isHideItem={isHideItem}
                    />
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </DragableObject>
          ))}
      </SortableContext>
    </DndContext>
  );
};

export default TaskBreakdownForm;
